// EventCardOrganizer.js
import React, { useRef, useState } from 'react';
import { IoLocationOutline } from "react-icons/io5";
import IconButton from '../../shared/IconButton';
import { FaCheck } from "react-icons/fa";
import { MdOutlineCancel } from "react-icons/md";
import { AnalyticIcon, CalendarIcon, DownloadIcon, DownloadIcon2, EditIcon, ShareIcon } from '../../../assets/icons/icons';
import Badge from '../../shared/Badge';
import { useReactToPrint } from "react-to-print";
import UrlInput from '../../shared/UrlInput';
import { apiConnecter } from '../../../services/apiConnector';
import Modal from "../../shared/ErrorModal";
import { Link, useNavigate } from "react-router";
import { exportEventAsCSV } from '../../../services/exportEventAsCSV';
import { exportEventAsReport } from '../../../services/exportEventAsTable';
import { VscFeedback } from 'react-icons/vsc';
import { BiExport } from "react-icons/bi";
import API_ENDPOINTS from '../../../constant/EndPoints';
import { getEngagementRate } from '../../../services/getEngagementRate';
/**
 * @typedef {object} Event
 * @property {string} _id - The unique ID of the event.
 * @property {string} [eventName="Platform Configuration"] - The name of the event.
 * @property {string} [status] - The current status of the event ('Active', 'Completed', 'Upcoming', etc.).
 * @property {number} [expectedAttendees] - The estimated number of attendees.
 * @property {number} [clickCount] - The total number of taps/clicks recorded *during* the event.
 * @property {number} [postEventClickCount] - Clicks recorded *after* the event date.
 * @property {string} [eventStartDate] - The start date string (ISO format, first 10 characters used for display).
 * @property {string} [eventEndDate] - The end date string (ISO format, first 10 characters used for display).
 * @property {string} [location] - The physical location of the event.
 * @property {string} [baseUrl] - The static URL encoded on the NFC bracelet/tag.
 * @property {string} [destinationUrl] - The URL the tap redirects to, which is editable.
 */

/**
 * @typedef {object} UserInfo
 * @property {string} token - The user's authentication token.
 * @property {string} role - The user's role (e.g., 'Organizer', 'Admin').
 */

/**
 * EventCardOrganizer is a card component specifically for event organizers to view key event metrics
 * and manage editable fields like the Destination URL and Expected Attendance.
 *
 * @param {object} props - The component's props.
 * @param {Event} props.event - The event data object to display and manage.
 * @param {UserInfo} props.userInfo - The current user's information, primarily used for authorization token access.
 * @param {function(): void} props.getAllEventsDataHandler - A function to re-fetch and update the parent component's event list after a successful edit.
 * @returns {React.ReactElement} The rendered Event Card for Organizers.
 */
const EventCardOrganizer = ({ event, userInfo, getAllEventsDataHandler }) => {
    console.log("--------event", event)

    /* ****************************************************************
    *                            STATES                             *
    ******************************************************************/
    const expectedAttendees = event?.expectedAttendees ?? 0;
    const navigate = useNavigate();
    const clickCount = event?.clickCount ?? 0;
    // const uniqueUsersOfIpaddress=event
    const postEventClickCount = event?.postEventClickCount ?? 0;
    const engagementRate = getEngagementRate(expectedAttendees, clickCount);
    const postEventEngagementRate = getEngagementRate(expectedAttendees, postEventClickCount);
    const uniqueUsers = event?.uniqueUsers;

    // state for editing destination URL
    const [newDestinationUrl, setNewDestinationUrl] = useState(event?.destinationUrl?.trim() ? event?.destinationUrl : "");
    const [newExpectedAttendees, setNewExpectedAttendees] = useState(event?.expectedAttendees ?? 0);
    const [isEditing, setIsEditing] = useState(false);
    const [isEditingAttendece, setIsEditingAttendece] = useState(false);
    const [loading, setLoading] = useState(false);

    const cardRef = useRef(null);



    /* ****************************************************************
    *                            SERVICES                             *
    ******************************************************************/

    /**
      * Calls the API to update the event's destination URL.
      * @async
      * @returns {Promise<void>}
      */
    const updateDesitinationUrl = async () => {
        try {
            setLoading(true);
            const response = await apiConnecter(
                "PUT",
                `${API_ENDPOINTS.REACT_APP_UPDATE_DESTINATION_URL_END_POINT}/${event._id}`,
                {
                    destinationUrl: newDestinationUrl
                },
                { authorization: `Bearer ${userInfo.token}` }
            );
            Modal({
                title: "Success",
                message: response.data.message,
            });
            getAllEventsDataHandler();
            setIsEditing(false);
        } catch (error) {
            setNewDestinationUrl(event?.destinationUrl);
            setNewExpectedAttendees(event?.expectedAttendees); // Revert on error
            console.error(error);
            Modal({
                title: "Error",
                message: error.response.data.message,
            });
        } finally {
            setLoading(false);
        }
    }

    /**
     * Calls the API to update the event's expected attendees count.
     * @async
     * @returns {Promise<void>}
     */
    const updateExpectedAttendees = async () => {
        try {
            setLoading(true);
            const response = await apiConnecter(
                "PUT",
                `${API_ENDPOINTS.REACT_APP_UPDATE_DESTINATION_URL_END_POINT}/${event._id}`,
                {
                    expectedAttendees: newExpectedAttendees, // Send the new expected attendees
                },
                { authorization: `Bearer ${userInfo.token}` }
            );
            Modal({
                title: "Success",
                message: "Expetected attendence updated successfully",
            });
            getAllEventsDataHandler();
            setIsEditing(false);
        } catch (error) {
            setNewDestinationUrl(event?.destinationUrl);
            setNewExpectedAttendees(event?.expectedAttendees); // Revert on error
            console.error(error);
            Modal({
                title: "Error",
                message: error.response.data.message,
            });
        } finally {
            setLoading(false);
        }
    }

    /* ****************************************************************
    *                            HELPER FUNCTION                      *
    ******************************************************************/

    /**
     * Validates the new destination URL and triggers the API update.
     * @async
     * @returns {Promise<void>}
     */
    const handleSave = async () => {
        if (loading) return;

        // Validate the destination URL
        if (newDestinationUrl.trim() === "" || newDestinationUrl === "N/A") {
            Modal({
                title: "Error",
                message: "Destination URL cannot be empty.",
            });
            return;
        }

        if (newDestinationUrl && (!/^https?:\/\/.+\..+/.test(newDestinationUrl))) {
            Modal({
                title: "Error",
                message: "Destination URL must be a valid URL",
            });
            return;
        }
        // Proceed to update
        updateDesitinationUrl();
    };

    /**
     * Validates the new expected attendance count and triggers the API update.
     * @async
     * @returns {Promise<void>}
     */
    const handleSaveExpectedAttendence = async () => {
        if (loading) return;

        // Validate expected attendees
        if (!String(newExpectedAttendees).trim()) {
            Modal({
                title: "Error",
                message: "Expected Attendance be empty",
            });
            return
        }
        if (isNaN(newExpectedAttendees) || Number(newExpectedAttendees) <= 0) {
            Modal({
                title: "Error",
                message: "Expected Attendance must be a positive number.",
            });
            return
        }
        console.log(Number(newExpectedAttendees), Number(event.expectedAttendees), "XXXXXXXXXXX")
        if (Number(newExpectedAttendees) == Number(event.expectedAttendees)) {
            Modal({
                title: "Error",
                message: "Expected attendees must be a different number.",
            });
            return;
        }
        if (newExpectedAttendees <= 0) {
            Modal({
                title: "Error",
                message: "Expected attendees must be a positive number.",
            });
            return;
        }
        // Proceed to update
        updateExpectedAttendees();
    };

    /**
    * Handles the export of event data as a CSV file.
    * @returns {void}
    */
    const handlePrint = () => {
        exportEventAsCSV(event)
    }

    /**
     * Cancels the destination URL edit mode and reverts the URL state.
     * @returns {void}
     */
    const handleCancel = () => {
        setIsEditing(false)
        setNewDestinationUrl(event.destinationUrl ? event.destinationUrl : "")
    };

    /**
     * Navigates the user to the analytics page for the current event, passing necessary state.
     * @param {string} eventId - The unique ID of the event.
     * @param {number} taps - Total number of taps.
     * @param {string} engagement - The pre-calculated engagement rate string.
     * @param {number} postClick - Total post-event clicks.
     * @param {number} attendance - Expected attendance.
     * @returns {void}
     */
    const handleAnalyticsClick = (eventId, taps, engagement, postClick, attendance) => {
        const tapsNum = Number(taps) || 0;
        const postClickNum = Number(postClick) || 0;
        const postClickRate = getEngagementRate(tapsNum, postClickNum);
        navigate(`/admin/analytics/${eventId}`, {
            state: {
                totalTaps: tapsNum,
                engagementRate: engagement,
                postClickRate: `${postEventEngagementRate}`,
                attendance: attendance,
            }
        });
    };

    /**
     * Navigates the user to the feedback page for the current event.
     * @returns {void}
     */
    const handleFeedbackClick = () => {
        navigate(`/admin/feedback?eventId=${event._id}`)
    }

    return (
        <div
            ref={cardRef}
            className="bg-[var(--color-surface-background)] rounded-2xl border border-[var(--border-color)] p-4 sm:p-6 w-full"
        >
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div className="flex flex-wrap items-center gap-2 justify-between">
                    <h3 className="text-lg font-semibold text-[var(--primary-text-color)] break-words">
                        {event?.eventName ?? "Platform Configuration"}
                    </h3>
                    {event?.status && (
                        <Badge
                            label={event.status}
                            bgColor={
                                event.status === 'Active'
                                    ? 'bg-[#fafafa]'
                                    : event.status === 'Completed'
                                        ? 'bg-[#262626]'
                                        : 'bg-transparent'
                            }
                            textColor={event.status === 'Completed' ? 'text-white' : 'text-black'}
                            borderColor={event.status === 'Upcoming' ? 'border-gray-700' : ''}
                        />
                    )}
                </div>
                <div className="flex gap-2 flex-wrap">
                    <IconButton
                        icon={BiExport}
                        label="Export"
                        onClick={handlePrint}
                        hoverColor="hover:bg-gray-600"
                        bgColor="bg-[var(--color-surface-background)]"
                        border={true}
                        disabled={loading}
                    />
                    {/* <IconButton
                        icon={AnalyticIcon}
                        color="text-[var(--color-primary)]"
                        label="View Analytics"
                        onClick={() =>
                            handleAnalyticsClick(event?._id, event?.clickCount, engagementRate, event?.postEventClickCount, event?.expectedAttendees)
                        }
                        hoverColor="hover:bg-gray-600"
                        bgColor="bg-[var(--color-surface-background)]"
                        border={true}
                    /> */}
                    <button
                        className={`text-[var(--primary-text-color)] px-4 py-2 rounded-lg flex items-center space-x-2 cursor-pointer bg-[var(--color-surface-background)] hover:bg-gray-600 border border-[var(--border-color)]`}
                        onClick={() => { handleFeedbackClick() }}
                    >
                        <span className='text-xl text-[var(--color-primary)]' ><VscFeedback /></span>
                    </button>
                </div>
            </div>

            {/* Date + Location */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4 text-sm text-[var(--primary-text-color)] mb-4">
                <div className="flex items-center gap-1">
                    <CalendarIcon className="w-4 h-4 shrink-0" />
                    <span className="truncate">
                        {event?.eventStartDate?.slice(0, 10) ?? "TBD"} -  {event?.eventEndDate?.slice(0, 10) ?? "TBD"} • {event?.location ?? "Location TBD"}
                    </span>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-3">
                <div className="bg-[var(--color-surface-input)] flex flex-col items-center justify-center p-3 rounded-xl">
                    <div className="text-xl sm:text-2xl text-[var(--color-primary)] font-semibold">
                        {expectedAttendees}
                    </div>
                    <div className="text-[var(--primary-text-color)] text-xs sm:text-sm">Attendance</div>
                </div>
                <div className="bg-[var(--color-surface-input)] flex flex-col items-center justify-center p-3 rounded-xl">
                    <div className="text-xl sm:text-2xl text-[var(--color-primary)] font-semibold">
                        {clickCount}
                    </div>
                    <div className="text-[var(--primary-text-color)] text-xs sm:text-sm">Total Taps</div>
                </div>
                <div className="bg-[var(--color-surface-input)] flex flex-col items-center justify-center p-3 rounded-xl">
                    <div className="text-xl sm:text-2xl text-[var(--color-primary)] font-semibold">
                        {uniqueUsers}
                    </div>
                    <div className="text-[var(--primary-text-color)] text-xs sm:text-sm">Unique Users</div>
                </div>
                <div className="bg-[var(--color-surface-input)] flex flex-col items-center justify-center p-3 rounded-xl">
                    <div className="text-xl sm:text-2xl text-[var(--color-primary)] font-semibold">
                        {engagementRate}
                    </div>
                    <div className="text-[var(--primary-text-color)] text-xs sm:text-sm">Engagement Rate</div>
                </div>
                <div className="bg-[var(--color-surface-input)] flex flex-col items-center justify-center p-3 rounded-xl">
                    <div className="text-xl sm:text-2xl text-[var(--color-primary)] font-semibold">
                        {postEventEngagementRate}%
                    </div>
                    <div className="text-[var(--primary-text-color)] text-xs sm:text-sm">Post-Event Retention</div>
                </div>
            </div>

            {/* URLs */}
            <div className="space-y-6">
                {/* Bracelet URL */}
                <div className="flex flex-col text-sm">
                    <span className="text-[var(--primary-text-color)] mb-2">Bracelet URL:</span>
                    <div className="flex flex-col sm:flex-row gap-2">
                        <div className="flex-1 w-full">
                            <UrlInput label={event?.baseUrl?.slice(0, 50) + "..." ?? "https://example.com/bracelet"} />
                        </div>
                        <Link target="_blank" rel="noopener noreferrer" to={event?.baseUrl} className="shrink-0">
                            <span className="border bg-[var(--color-primary)] border-[var(--border-color)] p-2 flex justify-center items-center rounded hover:bg-gray-600 cursor-pointer">
                                <ShareIcon />
                            </span>
                        </Link>
                    </div>
                    <div className="text-[var(--color-grey)] text-xs mt-2">
                        This URL is encoded in your NFC bracelets (read-only)
                    </div>
                </div>

                {/* Destination URL */}
                <div className="flex flex-col text-sm">
                    <span className="text-[var(--primary-text-color)] mb-2">Destination URL:</span>
                    <div className="flex flex-col sm:flex-row gap-2">
                        <div className="flex-1 w-full">
                            {isEditing ? (
                                <input
                                    type="text"
                                    className="w-full bg-[var(--color-surface-input)] text-[var(--primary-text-color)] rounded p-2"
                                    value={newDestinationUrl !== "N/A" ? newDestinationUrl : ""}
                                    placeholder='Ex. https://example.com'
                                    onChange={(e) => setNewDestinationUrl(e.target.value)}
                                    disabled={loading}
                                />
                            ) : (<UrlInput label={newDestinationUrl !== "" ? newDestinationUrl?.slice(0, 50) + "..." : "No destination set"} />

                            )}
                        </div>
                        <div className="flex gap-2">
                            {isEditing ? (
                                <>
                                    <span
                                        onClick={handleCancel}
                                        className={`border border-[var(--border-color)] p-2 flex justify-center items-center rounded hover:bg-gray-600 cursor-pointer bg-[var(--color-primary)] ${loading ? "pointer-events-none cursor-not-allowed" : ""
                                            }`}
                                    >
                                        <MdOutlineCancel style={{ color: "white", fontSize: 16 }} />
                                    </span>
                                    <span
                                        onClick={handleSave}
                                        disabled={loading}
                                        className="border border-[var(--border-color)] p-2 flex justify-center items-center rounded hover:bg-gray-600 cursor-pointer bg-[var(--color-primary)]"
                                    >
                                        {loading ? (
                                            <div className="w-5 h-5 border-4 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin"></div>
                                        ) : (
                                            <FaCheck style={{ color: "white" }} />
                                        )}
                                    </span>
                                </>
                            ) : (
                                <span
                                    onClick={() => setIsEditing(true)}
                                    className="border border-[var(--border-color)] bg-[var(--color-primary)] p-2 flex justify-center items-center rounded hover:bg-gray-600 cursor-pointer"
                                >
                                    <EditIcon />
                                </span>
                            )}
                            <Link target="_blank" rel="noopener noreferrer" to={event.destinationUrl} className="shrink-0">
                                <span className="border border-[var(--border-color)] bg-[var(--color-primary)] p-2 flex justify-center items-center rounded hover:bg-gray-600 cursor-pointer">
                                    <ShareIcon className="" />
                                </span>
                            </Link>
                        </div>
                    </div>
                    <div className="text-[var(--color-grey)] text-xs mt-2">
                        Where attendees are redirected when they tap their bracelet
                    </div>
                </div>
                {/* Expected Attendance */}
                <div className="flex flex-col text-sm">
                    <span className="text-[var(--primary-text-color)] mb-2">Expected Attendance:</span>
                    <div className="flex flex-col sm:flex-row gap-2">
                        <div className="flex-1 w-full">
                            {isEditingAttendece ? (
                                <input
                                    type="text"
                                    className="w-full bg-[var(--color-surface-input)] text-[var(--primary-text-color)] rounded p-2 "
                                    value={newExpectedAttendees}
                                    placeholder="Enter expected attendees"
                                    onChange={(e) => setNewExpectedAttendees(e.target.value)}
                                    disabled={loading}

                                />
                            ) : (
                                <div className="w-full bg-[var(--color-surface-input)] text-[var(--primary-text-color)] p-2 rounded">
                                    {newExpectedAttendees || "No attendees set"}
                                </div>
                            )}
                        </div>
                        <div className="flex gap-2">
                            {isEditingAttendece ? (
                                <>
                                    <span
                                        onClick={() => {
                                            setIsEditingAttendece(false)
                                            setNewExpectedAttendees(event.expectedAttendees)
                                        }}
                                        className={`border border-[var(--border-color)] p-2 flex justify-center items-center rounded hover:bg-gray-600 cursor-pointer bg-[var(--color-primary)] ${loading ? "pointer-events-none cursor-not-allowed" : ""}`}
                                    >
                                        <MdOutlineCancel style={{ color: "white", fontSize: 16 }} />
                                    </span>
                                    <span
                                        onClick={handleSaveExpectedAttendence}
                                        disabled={loading}
                                        className="border border-[var(--border-color)] p-2 flex justify-center items-center rounded hover:bg-gray-600 cursor-pointer bg-[var(--color-primary)]"
                                    >
                                        {loading ? (
                                            <div className="w-5 h-5 border-4 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin"></div>
                                        ) : (
                                            <FaCheck style={{ color: "white" }} />
                                        )}
                                    </span>
                                </>
                            ) : (
                                <span
                                    onClick={() => setIsEditingAttendece(true)}
                                    className="border border-[var(--border-color)] p-2 flex justify-center items-center rounded hover:bg-gray-600 cursor-pointer bg-[var(--color-primary)]"
                                >
                                    <EditIcon />
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="text-[var(--color-grey)] text-xs mt-2">
                        The number of attendees expected for the event.
                    </div>
                </div>

            </div>
        </div>

    );
};

export default EventCardOrganizer;
